import { Menu, MenuItem, PopoverOrigin, Stack, styled, Tooltip, TooltipProps } from '@mui/material'
import { MouseEvent, ReactElement, ReactNode, useState } from 'react'
import { IconButton, IconButtonProps } from './IconButton'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { DotsThreeVerticalIcon, IconContext } from '@phosphor-icons/react'
import { Typography } from '../../text'

export type Option = {
    title: string
    callback: () => void
    icon?: ReactNode
    danger?: boolean
    disabled?: boolean
    tooltip?: string
}

type IconButtonWithOptionsProps = {
    options: Option[]
    icon?: ReactElement
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
    optionTooltipPlacement?: TooltipProps['placement']
} & Omit<IconButtonProps, 'onClick' | 'icon'>

export function IconButtonWithOptions({
    options,
    icon = (
        <DotsThreeVerticalIcon style={{ scale: '1.1' /* This scale is needed on HD monitors so the dots do not look like mini lines */ }} />
    ),
    anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
    transformOrigin = { vertical: 'top', horizontal: 'right' },
    optionTooltipPlacement = 'top',
    ...iconButtonProps
}: IconButtonWithOptionsProps) {
    const [anchorEl, setAnchorEl] = useState<undefined | HTMLElement>()

    const onClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        event.preventDefault()
        setAnchorEl(event.currentTarget)
    }

    const onClose = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        event.preventDefault()
        setAnchorEl(undefined)
    }

    return (
        <>
            <IconButton onClick={onClick} icon={icon} {...iconButtonProps} />
            <StyledMenu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={onClose}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
            >
                {options.map((option) => (
                    <Tooltip
                        title={option.tooltip}
                        placement={optionTooltipPlacement}
                        sx={{ zIndex: 2000 }}
                        key={`menu-item-${option.title}`}
                    >
                        <span style={{ display: 'block' }}>
                            <StyledMenuItem
                                onClick={(event) => {
                                    event.stopPropagation()
                                    if (option.disabled) {
                                        return
                                    }
                                    option.callback()
                                    setAnchorEl(undefined)
                                }}
                                disableRipple
                                disabled={option.disabled}
                            >
                                <IconContext.Provider
                                    value={{
                                        color: option.danger ? tokens.ColorIconDanger : tokens.ColorIconBoldest,
                                        size: tokens.SizingIconS,
                                        weight: 'bold',
                                    }}
                                >
                                    <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                        {option.icon}
                                        <Typography
                                            variant="textM"
                                            color={option.danger ? tokens.ColorTextDangerSubtle : tokens.ColorTextBold}
                                        >
                                            {option.title}
                                        </Typography>
                                    </Stack>
                                </IconContext.Provider>
                            </StyledMenuItem>
                        </span>
                    </Tooltip>
                ))}
            </StyledMenu>
        </>
    )
}

const StyledMenuItem = styled(MenuItem)(() => ({
    height: '32px',
    minHeight: 'auto',
    padding: `0 ${tokens.SizingSpacing150}`,
}))

const StyledMenu = styled(Menu)(() => ({
    '& .MuiMenu-list': {
        padding: `${tokens.SizingSpacing050} 0`,
    },
}))
